import { MembershipBreakdownItem } from "../../../model/members/MembershipMetrics";
import { SignupOption } from "../../../model/client/ClientSettings";

/**
 * Relabel a breakdown whose names are stored option CODES using the client's configured options.
 *
 * The metrics endpoint groups by the raw stored value ("WalkingBy", "WebsiteGoogle"), because that is
 * the only stable identity across a rename of the display text. An unmatched code is passed through
 * unchanged rather than dropped or blanked — it means a member was captured under an option the client
 * has since removed (or the seeded "Member" code retired in FORT-349), and hiding those rows would
 * silently shrink the totals. Counts are re-aggregated after mapping so two codes sharing a label
 * collapse into one row instead of rendering as duplicates.
 *
 * `options` is tolerated as null/undefined: client config is fetched at runtime and a tenant that has
 * never saved signup settings can legitimately serve a payload with the list absent.
 */
export function relabelBreakdown(
  items: MembershipBreakdownItem[],
  options: SignupOption[] | undefined | null
): MembershipBreakdownItem[] {
  const labelByValue = new Map((options ?? []).map((o) => [o.value, o.label]));
  const totals = new Map<string, number>();

  items.forEach((item) => {
    const label = labelByValue.get(item.name) ?? item.name;
    totals.set(label, (totals.get(label) ?? 0) + item.count);
  });

  return Array.from(totals, ([name, count]) => ({ name, count })).sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name)
  );
}
